import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { CalendarDays, ChevronLeft, ChevronRight, FileSearch, Lock, ScanLine, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DataTableRow } from '@/components/ui/data-table-row';
import { TableSkeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FAMILIES, FAMILY_ORDER } from '../config/families';
import { registryColumns } from './registry-columns';
import {
  DocLinkStatus,
  type DocumentListItem,
  type DocumentRegistryQuery,
  type RegistryIdentificationFilter,
} from '../types/documentCapture.types';
import { LINK_STATUS } from '../config/families';
import {
  DEFAULT_REGISTRY_PAGE_SIZE,
  REGISTRY_IDENTIFICATION_OPTIONS,
  REGISTRY_IDENTIFICATION_TOOLTIP,
} from '../config/verification';

/** Remote search must not fire per keystroke. */
const SEARCH_DEBOUNCE_MS = 250;

interface RegistryPanelProps {
  documents: DocumentListItem[];
  total: number;
  loading: boolean;
  error: string | null;
  query: DocumentRegistryQuery;
  /** Families this user may view — chips outside this list are locked, never silently missing. */
  visiblePrefixes: string[];
  onQueryChange: (next: DocumentRegistryQuery) => void;
  onRetry: () => void;
}

export function RegistryPanel({
  documents,
  total,
  loading,
  error,
  query,
  visiblePrefixes,
  onQueryChange,
  onRetry,
}: RegistryPanelProps) {
  const navigate = useNavigate();
  // Search is local state so typing re-renders only this input, never the table below
  // (UI performance gate #6). The debounced value is what reaches the query.
  const [searchText, setSearchText] = useState(query.q ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onQueryChangeRef = useRef(onQueryChange);
  onQueryChangeRef.current = onQueryChange;
  const queryRef = useRef(query);
  queryRef.current = query;

  useEffect(() => {
    if (searchText === (queryRef.current.q ?? '')) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onQueryChangeRef.current({ ...queryRef.current, q: searchText || undefined, page: 1 });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchText]);

  const selectedTypes = useMemo(() => new Set(query.types ?? []), [query.types]);

  const toggleType = (prefix: string) => {
    const next = new Set(selectedTypes);
    if (next.has(prefix)) next.delete(prefix);
    else next.add(prefix);
    onQueryChange({ ...query, types: next.size ? [...next] : undefined, page: 1 });
  };

  // Pagination is SERVER-side (the query carries page/pageSize and the response carries the true
  // total) — never a client slice, because the table only ever holds the current page.
  // Keep this in step with the two other places the default is written — the store's initial
  // `registryQuery` and the URL seed in DocumentCapturePage. If they disagree, the first paint
  // pages by one number and the refetch pages by another.
  const pageSize = query.pageSize ?? DEFAULT_REGISTRY_PAGE_SIZE;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  // Clamp: a filter that shrinks the result set can strand the view past the last page, which
  // renders as a confusing empty table rather than "you filtered everything out".
  const page = Math.min(Math.max(1, query.page ?? 1), pageCount);
  const from = (page - 1) * pageSize;
  const pageRange =
    total === 0
      ? 'No documents'
      : `${from + 1}–${Math.min(from + pageSize, total)} of ${total} ${total === 1 ? 'document' : 'documents'}`;

  const hasDateFilter = Boolean(query.scannedFrom || query.tranFrom);
  const clearDates = () =>
    onQueryChange({ ...query, scannedFrom: undefined, scannedTo: undefined, tranFrom: undefined, tranTo: undefined, page: 1 });

  const table = useReactTable({
    data: documents,
    columns: registryColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="flex flex-col">
      {/* ---- filters ---- */}
      <div className="space-y-3 border-b border-border p-4">
        <div className="flex flex-wrap items-center gap-1.5">
          {FAMILY_ORDER.map((prefix) => {
            const family = FAMILIES[prefix];
            const permitted = visiblePrefixes.includes(prefix);
            const active = selectedTypes.has(prefix);

            // A family the user cannot view is shown LOCKED rather than hidden: the narrowing is
            // meant to be visible, so nobody wonders why their colleague sees more rows.
            if (!permitted) {
              return (
                <Tooltip key={prefix}>
                  <TooltipTrigger asChild>
                    <span className="inline-flex cursor-not-allowed items-center gap-1 rounded-full border border-border bg-muted px-2.5 py-1 text-xs text-muted-foreground opacity-60">
                      <Lock className="h-3 w-3" aria-hidden />
                      {prefix}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{family.label} — you don’t have access to this type</TooltipContent>
                </Tooltip>
              );
            }

            const FamilyIcon = family.icon;
            return (
              <Tooltip key={prefix}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => toggleType(prefix)}
                    aria-pressed={active}
                    aria-label={`${family.label} — filter`}
                    style={{ '--fam': family.hue } as React.CSSProperties}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors',
                      active
                        ? 'border-[hsl(var(--fam)/0.4)] bg-[hsl(var(--fam)/0.16)] text-[hsl(var(--fam))]'
                        : 'border-border bg-card text-muted-foreground hover:bg-accent'
                    )}
                  >
                    {/* The glyph is what ties the chip to the tile on every row below it. Without it
                        the filter row and the table looked like two unrelated vocabularies. */}
                    <FamilyIcon className="h-3 w-3 flex-shrink-0" aria-hidden />
                    {prefix}
                  </button>
                </TooltipTrigger>
                <TooltipContent>{family.label}</TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        {/*
          ONE compact row, labels INSIDE the controls — the prototype's layout. The previous version
          stacked a `<label>` above each field, which cost ~250px of vertical space before a single
          row of the table appeared; on a 900px screen the registry opened with the filters filling
          the panel and the results below the fold. Every control keeps an accessible name (`sr-only`
          label or `aria-label`) plus its tooltip, so nothing is lost but the height.
        */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[150px] flex-1">
            <label htmlFor="dc-search" className="sr-only">
              Search by number or entity
            </label>
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              id="dc-search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search number or entity…"
              className="h-9 pl-8"
            />
          </div>

          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex h-9 items-center gap-1.5 rounded-md border border-input bg-card pl-2.5 pr-1">
                <ScanLine className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" aria-hidden />
                <span className="text-xs text-muted-foreground">Scanned</span>
                <label htmlFor="dc-scanned" className="sr-only">
                  Scanned date
                </label>
                <input
                  id="dc-scanned"
                  type="date"
                  value={query.scannedFrom ?? ''}
                  // Exact date: from == to. The server widens it to the full Philippine day.
                  onChange={(e) =>
                    onQueryChange({
                      ...query,
                      scannedFrom: e.target.value || undefined,
                      scannedTo: e.target.value || undefined,
                      page: 1,
                    })
                  }
                  className="w-[104px] bg-transparent text-xs text-foreground outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              The day the paper was scanned into DOP — not the accounting date.
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex h-9 items-center gap-1.5 rounded-md border border-input bg-card pl-2.5 pr-1">
                <CalendarDays className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" aria-hidden />
                <span className="text-xs text-muted-foreground">Tran date</span>
                <label htmlFor="dc-tran" className="sr-only">
                  Transaction date
                </label>
                <input
                  id="dc-tran"
                  type="date"
                  value={query.tranFrom ?? ''}
                  onChange={(e) =>
                    onQueryChange({
                      ...query,
                      tranFrom: e.target.value || undefined,
                      tranTo: e.target.value || undefined,
                      page: 1,
                    })
                  }
                  className="w-[104px] bg-transparent text-xs text-foreground outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              The NetSuite accounting date on the transaction — often earlier than the scan date.
            </TooltipContent>
          </Tooltip>

          <div>
            <label htmlFor="dc-link" className="sr-only">
              Link status
            </label>
            <Select
              value={query.linkStatus === undefined ? 'all' : String(query.linkStatus)}
              onValueChange={(value) =>
                onQueryChange({
                  ...query,
                  linkStatus: value === 'all' ? undefined : (Number(value) as DocLinkStatus),
                  page: 1,
                })
              }
            >
              <SelectTrigger id="dc-link" className="h-9 w-[190px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {Object.entries(LINK_STATUS).map(([value, config]) => (
                  <SelectItem key={value} value={value}>
                    {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/*
            86d3x48q7 T13 — identification provenance as a FILTER, never a column. The filter row
            carries no static-columns constraint, and "show me everything that got here on an AI
            suggestion" is a real question once the calibration window closes. A column would put a
            "Confirmed" chip on nearly every row, which is noise that trains people to ignore chips.
          */}
          <div>
            <Tooltip>
              <TooltipTrigger asChild>
                <label htmlFor="dc-identification" className="sr-only">
                  Identification
                </label>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">{REGISTRY_IDENTIFICATION_TOOLTIP}</TooltipContent>
            </Tooltip>
            <Select
              // 'all' sentinel, not '' — Radix Select rejects an empty string item value.
              value={query.verification ?? 'all'}
              onValueChange={(value) =>
                onQueryChange({
                  ...query,
                  verification: value === 'all' ? undefined : (value as RegistryIdentificationFilter),
                  page: 1,
                })
              }
            >
              <SelectTrigger id="dc-identification" className="h-9 w-[190px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any identification</SelectItem>
                {REGISTRY_IDENTIFICATION_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {hasDateFilter && (
            <Button variant="ghost" size="sm" onClick={clearDates} className="h-9">
              <X className="mr-1 h-3.5 w-3.5" aria-hidden />
              Clear dates
            </Button>
          )}
        </div>
      </div>

      {/* ---- results ---- */}
      {loading ? (
        <div className="p-4">
          <TableSkeleton rows={8} cols={7} />
        </div>
      ) : error ? (
        <div className="p-2">
          <EmptyState
            icon={FileSearch}
            title="Couldn’t load documents"
            description={error}
            action={
              <Button variant="outline" size="sm" onClick={onRetry}>
                Try again
              </Button>
            }
          />
        </div>
      ) : documents.length === 0 ? (
        <div className="p-2">
          <EmptyState
            icon={FileSearch}
            title="No documents match these filters"
            description="Try clearing a filter, or widening the date range."
          />
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      // Small-caps header, per the design bundle — it drops the header row back
                      // behind the data instead of competing with the document numbers below it.
                      <TableHead
                        key={header.id}
                        style={{ width: header.getSize() }}
                        className="text-[10.5px] font-semibold uppercase tracking-[0.05em]"
                      >
                        {typeof header.column.columnDef.header === 'function'
                          ? header.column.columnDef.header(header.getContext())
                          : header.column.columnDef.header}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {/* Whole-row click, as the prototype has it. Pointing at the document number was a
                    ~110px target in a ~1200px row, and every other cell looked inert. The number is
                    still a real <Link> — that is what keyboard, screen readers and ⌘-click use;
                    this only widens the mouse target. */}
                {table.getRowModel().rows.map((row) => (
                  <DataTableRow
                    key={row.id}
                    row={row}
                    onRowClick={(r) => navigate(`/d/${r.original.docNumber}`)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
          {/*
            Pagination (86d3x48q7, from the prototype). The footer used to print the TRUE total while
            the table showed only the first page — so "127 documents" sat under 25 rows and the other
            102 were unreachable by any control on the screen. The server has always paged; nothing
            drove it.
          */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <label htmlFor="dc-page-size">Rows per page</label>
              <select
                id="dc-page-size"
                value={pageSize}
                onChange={(e) => onQueryChange({ ...query, pageSize: Number(e.target.value), page: 1 })}
                className="h-8 rounded-md border border-input bg-card px-2 text-xs font-medium text-foreground"
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3">
              <span className="tabular-nums text-xs text-muted-foreground">{pageRange}</span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  disabled={page <= 1}
                  onClick={() => onQueryChange({ ...query, page: page - 1 })}
                >
                  <ChevronLeft className="mr-1 h-3.5 w-3.5" aria-hidden />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  disabled={page >= pageCount}
                  onClick={() => onQueryChange({ ...query, page: page + 1 })}
                >
                  Next
                  <ChevronRight className="ml-1 h-3.5 w-3.5" aria-hidden />
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
